// Versão: 1.01
// Data..: 2026-10-02
// Grade de mês reutilizável (sem biblioteca de calendário externa — React/CSS puro).
// Renderiza as 6 semanas a partir de um cursor (dia 1 do mês de referência), o
// cabeçalho dos dias da semana e a navegação (mês anterior/próximo/hoje). Cada
// célula de dia é renderizada com destaque de hoje/selecionado; o conteúdo plotado
// em cada dia é decidido por quem usa, via a prop renderDayItems.
// v1.01: função pura monthRange (intervalo ISO do mês visível) reutilizada pelas views.

import { type ReactNode } from 'react';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

// Converte ISO em value de <input datetime-local> no fuso local.
export function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

// Chave de dia (AAAA-MM-DD no fuso local) a partir de um ISO.
export function dayKey(iso: string): string {
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}

// Chave de dia (AAAA-MM-DD no fuso local) a partir de um Date.
export function dayKeyFromDate(d: Date): string {
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}

// Rótulo "Mês Ano" a partir de um cursor (ex.: "Outubro 2026").
export function monthLabelFromCursor(cursor: Date): string {
  return `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;
}

// Intervalo ISO do mês visível: do primeiro instante (dia 1, 00:00:00.000) ao
// último (dia 0 do mês seguinte = último dia, 23:59:59.999). Usado no GET por mês.
export function monthRange(cursor: Date): { from: string; to: string } {
  const from = new Date(cursor.getFullYear(), cursor.getMonth(), 1, 0, 0, 0, 0);
  const to = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function MonthGrid({
  cursor,
  selectedDay,
  onSelectDay,
  onPrev,
  onNext,
  onToday,
  renderDayItems,
}: {
  cursor: Date;
  selectedDay: string;
  onSelectDay: (dayKey: string) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  // Cada view decide o que plotar dentro da célula do dia.
  renderDayItems: (dayKey: string) => ReactNode;
}) {
  const todayKey = dayKeyFromDate(new Date());

  // Monta as semanas da grade do mês (inclui dias "vazios" do mês anterior/próximo).
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() - firstDay.getDay()); // volta até o domingo
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  const weeks: Date[][] = [];
  for (let w = 0; w < 6; w++) weeks.push(cells.slice(w * 7, w * 7 + 7));

  return (
    <>
      <div className="flex items-center justify-between mb-4" style={{ flexWrap: 'wrap', gap: 'var(--s2)' }}>
        <div className="flex items-center gap-2">
          <button className="btn btn-sm btn-ghost" onClick={onPrev} aria-label="Mês anterior">‹</button>
          <span className="font-semibold" style={{ minWidth: 160, textAlign: 'center' }}>
            {monthLabelFromCursor(cursor)}
          </span>
          <button className="btn btn-sm btn-ghost" onClick={onNext} aria-label="Próximo mês">›</button>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={onToday}>Hoje</button>
      </div>

      <div className="card card-flush" style={{ overflow: 'hidden' }}>
        {/* Cabeçalho dos dias da semana. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--border)' }}>
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-xs text-muted" style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{w}</div>
          ))}
        </div>
        {/* Grade do mês. */}
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {week.map((day) => {
              const key = dayKeyFromDate(day);
              const inMonth = day.getMonth() === cursor.getMonth();
              const isSelected = key === selectedDay;
              const isToday = key === todayKey;
              return (
                <button
                  key={key}
                  onClick={() => onSelectDay(key)}
                  style={{
                    minHeight: 76, padding: '6px', border: '1px solid var(--border)', background: 'transparent',
                    textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4,
                    opacity: inMonth ? 1 : 0.4,
                    outline: isSelected ? '2px solid var(--primary)' : 'none', outlineOffset: '-2px',
                  }}
                  aria-label={`Dia ${day.getDate()}`}
                >
                  <span
                    className="text-sm"
                    style={{
                      fontWeight: isToday ? 700 : 500,
                      color: isToday ? 'var(--primary)' : 'var(--text-2)',
                    }}
                  >
                    {day.getDate()}
                  </span>
                  {renderDayItems(key)}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}
